import type {
  Plan,
  Stage,
  CreatePlanDTO,
  UpdatePlanDTO,
  CreateStageDTO,
  UpdateStageDTO,
  GeneratePlanDTO,
  PlannerPreferences,
  PlannerDashboardSnapshot,
  PlannerDashboardAIInsights
} from '@my-roadtrip/types'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'
import { MAP_PRESET_IDS } from '@my-roadtrip/types'
import type { RedisClient } from '../../core/redis/client.js'
import { safeJsonParse } from '../../core/database/redis'
import { deleteKeysWithRetry } from '../../common/utils/redis-utils.js'
import { PlannerRepository } from './planner.repository'
import { RouteOptimizer } from './route-optimizer'
import * as Y from 'yjs'
import { PlannerAIService } from './planner-ai.service'
import { createLogger } from '../../common/utils/logger.js'
import { AIRepository } from '../ai/ai.repository.js'
import {
  buildPlannerInsights,
  buildDashboardHighlights,
  buildPlanSummaries,
  selectSpotlightPlan
} from './dashboard-insights.js'

const logger = createLogger('PlannerService')

export class PlannerService {
  private readonly cachePrefix = 'plan:'
  // ✅ Shorter TTL in dev (5 min), longer in prod (1 hour)
  private readonly cacheTTL = process.env.NODE_ENV === 'production' ? 3600 : 300
  private readonly cacheEnabled = process.env.ENABLE_REDIS_CACHE !== 'false' // ✅ Feature flag
  private readonly dashboardCachePrefix = 'planner:dashboard:'
  // ✅ Increased dashboard cache TTL: 10 min dev, 30 min prod (most data changes rarely)
  private readonly dashboardCacheTTL = process.env.NODE_ENV === 'production' ? 1800 : 600

  constructor(
    private readonly repository: PlannerRepository,
    private readonly redis: RedisClient,
    private readonly routeOptimizer: RouteOptimizer,
    private readonly aiService: PlannerAIService,
    private readonly aiRepository: AIRepository = new AIRepository()
  ) { }

  // ✅ Helper to safely invalidate cache (only if enabled)
  private async invalidateCache(...keys: string[]) {
    if (!this.cacheEnabled) return

    logger.debug('Invalidating cache', { keys, enabled: this.cacheEnabled })
    await Promise.all(keys.map(key => this.redis.del(key)))
    logger.debug('Cache invalidated successfully', { keysCount: keys.length })
  }

  private async invalidateDashboardCache(userId: string) {
    if (!this.cacheEnabled) {
      logger.debug('Dashboard cache disabled, skipping invalidation', { userId })
      return
    }
    const cacheKey = `${this.dashboardCachePrefix}${userId}`
    logger.debug('Invalidating dashboard cache', { userId })
    await this.redis.del(cacheKey)
  }

  // Plans
  async getPublicPlans(): Promise<Plan[]> {
    logger.debug('Fetching public plans', { cacheEnabled: this.cacheEnabled })

    // Cache key for public plans
    const cacheKey = `${this.cachePrefix}public`

    // Check cache only if enabled
    if (this.cacheEnabled) {
      const cached = await this.redis.get(cacheKey)
      const parsed = safeJsonParse<Plan[]>(cached, cacheKey)
      if (parsed) {
        logger.debug('Cache hit for public plans', { count: parsed.length })
        return parsed
      }
      logger.debug('Cache miss for public plans')
    }

    // Fetch from DB (public plans only)
    logger.info('Fetching public plans from database')
    const plans = await this.repository.findPublicPlans()
    logger.info('Public plans fetched', { count: plans.length })

    // Cache result (shorter TTL for public data: 5 minutes)
    if (this.cacheEnabled) {
      await this.redis.setex(cacheKey, 300, JSON.stringify(plans))
      logger.debug('Public plans cached', { ttl: 300 })
    }

    return plans
  }

  async getUserPlans(userId: string): Promise<Plan[]> {
    logger.debug('Fetching user plans', { userId, cacheEnabled: this.cacheEnabled })

    // ✅ Check cache only if enabled
    if (this.cacheEnabled) {
      const cacheKey = `${this.cachePrefix}user:${userId}`
      const cached = await this.redis.get(cacheKey)
      const parsed = safeJsonParse<Plan[]>(cached, cacheKey)
      if (parsed) {
        logger.debug('Cache hit for user plans', { userId, count: parsed.length })
        return parsed
      }
      logger.debug('Cache miss for user plans', { userId })
    }

    // Fetch from DB
    logger.info('Fetching user plans from database', { userId })
    const plans = await this.repository.findPlansByUserId(userId)
    logger.info('User plans fetched', { userId, count: plans.length })

    // Cache result only if enabled
    if (this.cacheEnabled) {
      const cacheKey = `${this.cachePrefix}user:${userId}`
      await this.redis.setex(cacheKey, this.cacheTTL, JSON.stringify(plans))
      logger.debug('User plans cached', { userId, ttl: this.cacheTTL })
    }

    return plans
  }

  async getDashboardSnapshot(userId: string): Promise<PlannerDashboardSnapshot> {
    const cacheKey = `${this.dashboardCachePrefix}${userId}`
    const spanId = logger.startSpan('dashboard:snapshot', { userId })
    let cacheHit = false

    try {
      if (this.cacheEnabled) {
        const cached = await this.redis.get(cacheKey)
        const parsed = safeJsonParse<PlannerDashboardSnapshot>(cached, cacheKey)
        if (parsed) {
          logger.debug('Dashboard snapshot cache hit', { userId })
          cacheHit = true
          return parsed
        }
        logger.debug('Dashboard snapshot cache miss', { userId })
      }

      const [plans, aiInsights, profile] = await Promise.all([
        this.getUserPlans(userId),
        this.getAIInsights(userId),
        this.repository.getUserProfile(userId)
      ])
      logger.debug('Building planner insights for dashboard', { userId, planCount: plans.length })
      const insights = buildPlannerInsights(plans)
      const highlights = buildDashboardHighlights(insights)
      const recentPlans = buildPlanSummaries(plans)
      const spotlightPlan = selectSpotlightPlan(plans)
      const preferences =
        this.repository.extractPlannerPreferences(profile) ?? this.defaultPreferences()

      const seasonRaw = profile?.season ?? null
      const season = seasonRaw
        ? {
          active: Boolean(seasonRaw.active ?? true),
          seasonId: typeof seasonRaw.seasonId === 'string' ? seasonRaw.seasonId : undefined,
          name: typeof seasonRaw.name === 'string' ? seasonRaw.name : undefined,
          level: typeof seasonRaw.level === 'number' ? seasonRaw.level : undefined,
          xp: typeof seasonRaw.xp === 'number' ? seasonRaw.xp : undefined,
          nextLevelXp:
            typeof seasonRaw.nextLevelXp === 'number'
              ? seasonRaw.nextLevelXp
              : typeof seasonRaw.nextLevelPoints === 'number'
                ? seasonRaw.nextLevelPoints
                : undefined,
          progressPercent:
            typeof seasonRaw.progressPercent === 'number'
              ? seasonRaw.progressPercent
              : typeof seasonRaw.progress === 'number'
                ? seasonRaw.progress
                : undefined,
          endsAt: typeof seasonRaw.endsAt === 'string' ? seasonRaw.endsAt : undefined
        }
        : undefined

      const gamificationRaw = profile?.gamification ?? null
      const gamification = gamificationRaw
        ? {
          level: typeof gamificationRaw.level === 'number' ? gamificationRaw.level : 0,
          points: typeof gamificationRaw.points === 'number' ? gamificationRaw.points : 0,
          nextLevelThreshold:
            typeof gamificationRaw.nextLevelPoints === 'number'
              ? gamificationRaw.nextLevelPoints
              : typeof gamificationRaw.nextLevelThreshold === 'number'
                ? gamificationRaw.nextLevelThreshold
                : 0
        }
        : undefined

      const snapshot: PlannerDashboardSnapshot = {
        insights,
        highlights,
        timeline: insights.timeline.nextStages.slice(0, 10),
        recentPlans,
        spotlightPlan,
        ai: aiInsights,
        season,
        gamification,
        preferences,
        updatedAt: new Date().toISOString()
      }

      if (this.cacheEnabled) {
        await this.redis.setex(cacheKey, this.dashboardCacheTTL, JSON.stringify(snapshot))
        logger.debug('Dashboard snapshot cached', { userId, ttl: this.dashboardCacheTTL })
      }

      logger.info('Dashboard snapshot generated', {
        userId,
        planCount: plans.length,
        upcomingStages: snapshot.timeline.length,
        hasAIInsights: Boolean(snapshot.ai)
      })
      return snapshot
    } finally {
      logger.endSpan(spanId, { cacheHit })
    }
  }

  async getPlannerPreferences(userId: string): Promise<PlannerPreferences> {
    logger.debug('Fetching planner preferences', { userId })
    const preferences = await this.repository.getPlannerPreferences(userId)
    if (!preferences) {
      logger.debug('Planner preferences not found, falling back to defaults', { userId })
    }
    return preferences ?? this.defaultPreferences()
  }

  async updatePlannerPreferences(userId: string, updates: Partial<PlannerPreferences>): Promise<PlannerPreferences> {
    logger.info('Updating planner preferences', {
      userId,
      updateFields: Object.keys(updates ?? {})
    })
    const current = await this.getPlannerPreferences(userId)
    const merged: PlannerPreferences = {
      ...current,
      ...updates,
      favoriteCategories: this.mergeCategories(current.favoriteCategories, updates.favoriteCategories),
      lastUsedCategories: (() => {
        const combined = this.mergeCategories(current.lastUsedCategories ?? [], updates.lastUsedCategories)
        return combined.length ? combined : undefined
      })(),
      defaultRadiusKm:
        typeof updates.defaultRadiusKm === 'number'
          ? this.normalizeRadius(updates.defaultRadiusKm)
          : current.defaultRadiusKm,
      panelOpen: typeof updates.panelOpen === 'boolean' ? updates.panelOpen : current.panelOpen,
      layoutPreset: updates.layoutPreset ?? current.layoutPreset,
      defaultContext: updates.defaultContext ?? current.defaultContext,
      mapPresetId:
        typeof updates.mapPresetId === 'string' && MAP_PRESET_IDS.includes(updates.mapPresetId)
          ? updates.mapPresetId
          : current.mapPresetId,
      updatedAt: new Date().toISOString()
    }

    await this.repository.setPlannerPreferences(userId, merged)
    await this.invalidateDashboardCache(userId)
    logger.debug('Planner preferences updated', { userId })
    return merged
  }

  private defaultPreferences(): PlannerPreferences {
    return {
      layoutPreset: 'balanced',
      panelOpen: true,
      defaultContext: 'pre',
      favoriteCategories: [],
      defaultRadiusKm: this.normalizeRadius(25),
      mapPresetId: 'exploration'
    }
  }

  private normalizeRadius(value?: number): number {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 25
    }
    return Math.min(Math.max(Math.round(value), 5), 120)
  }

  private mergeCategories(base: string[], incoming?: string[]): string[] {
    const result: string[] = []
    const source = Array.isArray(incoming) ? incoming : []
    const combined = [...source, ...base]

    for (const entry of combined) {
      if (!isString(entry)) {
        continue
      }
      const value = entry.trim()
      if (!value.length) {
        continue
      }
      if (!result.some(existing => existing.toLowerCase() === value.toLowerCase())) {
        result.push(value.slice(0, 40))
      }
      if (result.length >= 12) {
        break
      }
    }

    return result
  }

  private async getAIInsights(userId: string): Promise<PlannerDashboardAIInsights | undefined> {
    try {
      logger.debug('Fetching AI insights for dashboard', { userId })
      const insights = await this.aiRepository.getUserInsights(userId)

      if (!insights) {
        logger.debug('No AI insights available', { userId })
        return undefined
      }

      const usagePast30d = Array.isArray(insights.usageTrends?.lastSevenDays)
        ? insights.usageTrends.lastSevenDays.reduce((sum, day) => sum + (day.total ?? 0), 0)
        : 0

      const topCategories = Array.isArray(insights.poi?.topCategories)
        ? insights.poi.topCategories.slice(0, 6)
        : []

      const lastGeneratedAt = insights.lastGeneratedAt ?? null
      const cacheHitRatio =
        typeof insights.usageTrends?.cacheHitRatio === 'number'
          ? Number(insights.usageTrends.cacheHitRatio.toFixed(2))
          : undefined

      return {
        topCategories,
        lastGeneratedAt,
        usagePast30d,
        cacheHitRatio
      }
    } catch (error) {
      logger.warn('Failed to fetch AI insights for dashboard', { userId, error })
      return undefined
    }
  }

  private async invalidateDashboardCacheForPlan(planId: string) {
    try {
      logger.debug('Invalidating dashboard cache for plan owner', { planId })
      const plan = await this.repository.findPlanById(planId)
      if (plan) {
        await this.invalidateDashboardCache(plan.userId)
        logger.debug('Dashboard cache invalidated for owner', { planId, userId: plan.userId })
      } else {
        logger.warn('Unable to invalidate dashboard cache, plan not found', { planId })
      }
    } catch (error) {
      logger.warn('Failed to invalidate dashboard cache for plan', { planId, error })
    }
  }

  async getPlan(planId: string): Promise<Plan | null> {
    logger.debug('Fetching plan', { planId, cacheEnabled: this.cacheEnabled })

    // ✅ Check cache only if enabled
    if (this.cacheEnabled) {
      const cacheKey = `${this.cachePrefix}${planId}`
      const cached = await this.redis.get(cacheKey)
      const parsed = safeJsonParse<Plan>(cached, cacheKey)
      if (parsed) {
        logger.debug('Cache hit for plan', { planId })
        return parsed
      }
      logger.debug('Cache miss for plan', { planId })
    }

    // Fetch from DB
    logger.info('Fetching plan from database', { planId })
    const plan = await this.repository.findPlanById(planId)

    if (plan) {
      logger.info('Plan found', { planId, stagesCount: plan.stages?.length || 0 })

      if (this.cacheEnabled) {
        // Cache result only if enabled
        const cacheKey = `${this.cachePrefix}${planId}`
        await this.redis.setex(cacheKey, this.cacheTTL, JSON.stringify(plan))
        logger.debug('Plan cached', { planId, ttl: this.cacheTTL })
      }
    } else {
      logger.warn('Plan not found', { planId })
    }

    return plan
  }

  async createPlan(dto: CreatePlanDTO, userId: string): Promise<Plan> {
    logger.info('Creating plan', { userId, planName: dto.name })
    logger.debug('Plan creation details', { dto, userId })

    const plan = await this.repository.createPlan(dto, userId)
    logger.info('Plan created successfully', { planId: plan.id, userId, planName: plan.name })

    if (dto.isDefault) {
      await this.repository.setDefaultPlan(userId, plan.id)
      await this.invalidateCache(`${this.cachePrefix}user:${userId}`)
    }

    // Initialize CRDT document (only if collaboration enabled)
    if (process.env.ENABLE_COLLABORATION !== 'false') {
      logger.debug('Initializing CRDT for plan', { planId: plan.id })
      await this.initializeCRDT(plan.id)
      logger.debug('CRDT initialized', { planId: plan.id })
    }

    // Invalidate user plans cache
    await this.invalidateCache(`${this.cachePrefix}user:${userId}`)
    await this.invalidateDashboardCache(userId)

    return plan
  }

  async updatePlan(planId: string, updates: UpdatePlanDTO): Promise<Plan | null> {
    logger.info('Updating plan', { planId, updates: Object.keys(updates) })
    logger.debug('Plan update details', { planId, updates })

    const updated = await this.repository.updatePlan(planId, updates);

    if (updated) {
      logger.info('Plan updated successfully', { planId, userId: updated.userId })

      // Update cache (only if enabled)
      if (this.cacheEnabled) {
        const cacheKey = `${this.cachePrefix}${planId}`
        await this.redis.setex(cacheKey, this.cacheTTL, JSON.stringify(updated))
        logger.debug('Plan cache updated', { planId })
      }

      // Invalidate user plans cache
      await this.invalidateCache(`${this.cachePrefix}user:${updated.userId}`)

      if (updates.isDefault) {
        await this.repository.setDefaultPlan(updated.userId, updated.id)
        await this.invalidateCache(
          `${this.cachePrefix}${planId}`,
          `${this.cachePrefix}user:${updated.userId}`
        )
      }

      await this.invalidateDashboardCache(updated.userId)

      await this.withCRDTDocument(planId, doc => {
        this.bumpPlanVersion(doc)
        logger.debug('CRDT version bumped', { planId })
      })
    } else {
      logger.warn('Plan update failed - plan not found', { planId })
    }

    return updated
  }

  async getCurrentPlan(userId: string): Promise<Plan> {
    logger.info('Ensuring current plan', { userId })

    const defaultPlan = await this.repository.findDefaultPlanByUserId(userId)
    if (defaultPlan) {
      logger.debug('Found existing default plan', { userId, planId: defaultPlan.id })
      return defaultPlan
    }

    const mostRecent = await this.repository.findMostRecentPlanByUserId(userId)
    if (mostRecent) {
      logger.debug('Using most recent plan as default', { userId, planId: mostRecent.id })
      await this.repository.setDefaultPlan(userId, mostRecent.id)
      await this.invalidateCache(
        `${this.cachePrefix}user:${userId}`,
        `${this.cachePrefix}${mostRecent.id}`
      )
      const reloaded = await this.repository.findPlanById(mostRecent.id)
      return reloaded ?? mostRecent
    }

    logger.info('No plans found for user, creating default plan', { userId })
    const created = await this.createDefaultPlan(userId)
    return created
  }

  /**
   * Delete a plan and ALL related data.
   * 
   * Cascade delete includes:
   * - Firestore: plan, stages, shareLinks, budgets, expenses, plan_expenses, snapshots
   * - Redis: ydoc, presence, access cache (with retry logic)
   * - Cache invalidation for user dashboard
   * 
   * @param planId - The plan ID to delete
   * @param options - Delete options
   * @param options.softDelete - If true, marks as deleted for recovery (default: false for hard delete)
   */
  async deletePlan(planId: string, options: { softDelete?: boolean } = {}): Promise<void> {
    const { softDelete = false } = options
    logger.info('Deleting plan with full cascade', { planId, softDelete })

    const plan = await this.repository.findPlanById(planId)

    if (!plan) {
      logger.warn('Plan deletion skipped - plan not found', { planId })
      return
    }

    const userId = plan.userId
    logger.debug('Plan found for deletion', { planId, userId, stagesCount: plan.stages?.length || 0 })

    // 1. Delete all Firestore data via repository
    // SmartBatch handles 500 operation limit automatically
    const { deletedCount } = await this.repository.deletePlan(planId, { softDelete })
    logger.debug('Firestore data deleted', { planId, deletedCount })

    // 2. Delete Redis data with retry logic (only for hard delete)
    if (!softDelete) {
      const redisKeysToDelete = [
        `ydoc:${planId}`,           // CRDT document
        `presence:${planId}`,       // Presence data
        `plan:access:${planId}`,    // Access cache
      ]

      const redisResult = await deleteKeysWithRetry(this.redis, redisKeysToDelete, {
        maxRetries: 3,
        baseDelayMs: 100
      })

      if (redisResult.failed.length > 0) {
        logger.warn('Some Redis keys failed to delete', {
          planId,
          failed: redisResult.failed
        })
      }
      logger.debug('Redis data deleted', { planId, deleted: redisResult.deleted })
    }

    // 3. Invalidate caches
    await this.invalidateCache(
      `${this.cachePrefix}${planId}`,
      `${this.cachePrefix}user:${userId}`
    )
    await this.invalidateDashboardCache(userId)
    logger.debug('Caches invalidated', { planId, userId })

    logger.info('Plan deleted successfully with full cascade', {
      planId,
      userId,
      softDelete,
      deletedCount
    })
  }

  /**
   * Restore a soft-deleted plan
   */
  async restorePlan(planId: string): Promise<void> {
    logger.info('Restoring plan', { planId })
    await this.repository.restorePlan(planId)

    // Re-initialize CRDT if needed
    const plan = await this.repository.findPlanById(planId)
    if (plan) {
      await this.invalidateDashboardCache(plan.userId)
      logger.info('Plan restored successfully', { planId, userId: plan.userId })
    }
  }

  // Stages
  async getStage(stageId: string): Promise<Stage | null> {
    logger.debug('Fetching stage', { stageId })
    const stage = await this.repository.findStageById(stageId)

    if (stage) {
      logger.debug('Stage found', { stageId, planId: stage.planId })
    } else {
      logger.warn('Stage not found', { stageId })
    }

    return stage
  }

  async createStage(planId: string, dto: CreateStageDTO): Promise<Stage> {
    logger.info('Creating stage', { planId, stageName: dto.name })
    logger.debug('Stage creation details', { planId, dto })

    const stage = await this.repository.createStage(planId, dto)
    logger.info('Stage created successfully', { stageId: stage.id, planId, stageName: stage.name })

    // Invalidate plan cache
    await this.invalidateCache(`${this.cachePrefix}${planId}`)

    logger.debug('Upserting stage in CRDT', { planId, stageId: stage.id })
    await this.upsertStageInCRDT(planId, stage)
    await this.invalidateDashboardCacheForPlan(planId)

    return stage
  }

  async generatePlan(userId: string, payload: GeneratePlanDTO): Promise<Plan> {
    logger.info('AI plan generation requested', {
      userId,
      origin: payload.origin,
      destinationCount: payload.destinations?.length ?? 0,
      days: payload.days,
      travelMode: payload.travelMode
    })
    const spanId = logger.startSpan('ai:generatePlan', { userId })
    let generatedPlanId: string | undefined
    try {
      const summary = await this.aiService.generatePlan(payload, { userId })

      const stagePayloads = summary.stages.map((stage, index) => ({
        name: stage.name,
        type: stage.type,
        order: index,
        notes: stage.summary,
        location: {
          address: stage.location.address,
          coordinates: {
            lat: stage.location.coordinates.lat,
            lng: stage.location.coordinates.lng
          },
          placeId: stage.location.placeId,
          name: stage.location.name,
          lat: stage.location.coordinates.lat,
          lng: stage.location.coordinates.lng
        },
        estimatedArrival: this.safeParseDate(stage.arrivalTime),
        estimatedDeparture: this.safeParseDate(stage.departureTime),
        duration: stage.durationMinutes,
        day: stage.day
      }))

      const plan = await this.repository.createPlanWithStages(
        userId,
        {
          name: summary.name,
          description: summary.description,
          isPublic: false,
          settings: summary.settings,
          tags: summary.tags,
          aiMetadata: {
            model: summary.metadata.model,
            generatedAt: new Date(),
            strategy: summary.metadata.strategy,
            promptSummary: this.describePrompt(payload)
          }
        },
        stagePayloads
      )

      if (process.env.ENABLE_COLLABORATION !== 'false') {
        await this.initializeCRDT(plan.id)
        await this.syncStagesWithCRDT(plan.id, plan.stages)
      }

      await this.invalidateCache(`${this.cachePrefix}user:${userId}`)
      await this.invalidateDashboardCache(userId)

      generatedPlanId = plan.id
      logger.info('AI plan generation completed', { userId, planId: plan.id, stages: plan.stages.length })
      return plan
    } catch (error) {
      logger.error('AI plan generation failed', { userId, error })
      throw error
    } finally {
      logger.endSpan(spanId, { userId, planId: generatedPlanId })
    }
  }

  async updateStage(stageId: string, updates: UpdateStageDTO): Promise<Stage | null> {
    logger.info('Updating stage', { stageId, updateFields: Object.keys(updates ?? {}) })
    const updated = await this.repository.updateStage(stageId, updates)

    if (updated) {
      logger.debug('Stage updated successfully', { stageId, planId: updated.planId })
      // Invalidate plan cache
      await this.invalidateCache(`${this.cachePrefix}${updated.planId}`)
      await this.upsertStageInCRDT(updated.planId, updated)
      await this.invalidateDashboardCacheForPlan(updated.planId)
    } else {
      logger.warn('Stage update failed - not found', { stageId })
    }

    return updated
  }

  async deleteStage(stageId: string): Promise<void> {
    logger.info('Deleting stage', { stageId })

    // Fetch stage data BEFORE deletion to avoid race condition
    const stage = await this.repository.findStageById(stageId)

    if (!stage) {
      logger.warn('Stage deletion skipped - stage not found', { stageId })
      return
    }

    // Now delete the stage
    await this.repository.deleteStage(stageId)
    logger.info('Stage deleted from database', { stageId, planId: stage.planId })

    // Clean up related data using the cached stage info
    await this.invalidateCache(`${this.cachePrefix}${stage.planId}`)
    await this.removeStageFromCRDT(stage.planId, stageId)
    await this.invalidateDashboardCacheForPlan(stage.planId)
    logger.info('Stage deleted successfully', { stageId, planId: stage.planId })
  }

  async optimizeRoute(planId: string): Promise<Stage[]> {
    logger.info('Optimizing route', { planId })
    const plan = await this.repository.findPlanById(planId)

    if (!plan) {
      logger.warn('Route optimization failed - plan not found', { planId })
      throw new Error('Plan not found')
    }

    const optimized = this.routeOptimizer.optimizeRoute(plan.stages)
    logger.debug('Route optimization completed', { planId, stageCount: optimized.length })

    await this.repository.updateStagesBatch(optimized)

    // Invalidate plan cache
    await this.invalidateCache(`${this.cachePrefix}${planId}`)

    await this.syncStagesWithCRDT(planId, optimized)
    await this.invalidateDashboardCacheForPlan(planId)
    logger.info('Route optimized and persisted', { planId, stageCount: optimized.length })

    return optimized
  }

  async reorderStages(planId: string, stageIds: string[]): Promise<Stage[]> {
    logger.info('Reordering stages', { planId, count: stageIds.length })
    const plan = await this.repository.findPlanById(planId)

    if (!plan) {
      logger.warn('Reorder failed - plan not found', { planId })
      throw new Error('Plan not found')
    }

    const stageMap = new Map(plan.stages.map(stage => [stage.id, stage]))
    const reordered: Stage[] = []
    let order = 0

    for (const id of stageIds) {
      const stage = stageMap.get(id)
      if (!stage) continue
      reordered.push({ ...stage, order })
      stageMap.delete(id)
      order += 1
    }

    const remaining = Array.from(stageMap.values()).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    )

    for (const stage of remaining) {
      reordered.push({ ...stage, order })
      order += 1
    }

    if (!reordered.length) {
      logger.warn('Reorder skipped - no matching stages', { planId })
      return plan.stages
    }

    await this.repository.updateStagesBatch(reordered)
    await this.invalidateCache(`${this.cachePrefix}${planId}`)
    await this.invalidateDashboardCache(plan.userId)

    if (process.env.ENABLE_COLLABORATION !== 'false') {
      await this.syncStagesWithCRDT(planId, reordered)
    }

    logger.info('Stages reordered and persisted', { planId, count: reordered.length })
    return reordered
  }

  // CRDT initialization
  private async initializeCRDT(planId: string): Promise<void> {
    // ✅ Skip if collaboration disabled
    if (process.env.ENABLE_COLLABORATION === 'false') return

    const doc = new Y.Doc()
    const yMap = doc.getMap('plan')

    yMap.set('id', planId)
    yMap.set('stages', [])
    yMap.set('version', 0)

    const state = Y.encodeStateAsUpdate(doc)
    await this.redis.set(`ydoc:${planId}`, Buffer.from(state))
  }

  // Share plan
  async sharePlan(planId: string, userIds: string[], permissions: 'view' | 'edit'): Promise<Plan | null> {
    logger.info('Sharing plan', { planId, targetCount: userIds.length, permissions })
    const plan = await this.repository.findPlanById(planId)

    if (!plan) {
      logger.warn('Plan sharing failed - plan not found', { planId })
      return null
    }

    // Update sharedWith and sharedPermissions
    const sharedWith = Array.from(new Set([...(plan.sharedWith || []), ...userIds]))
    const sharedPermissions: Record<string, 'view' | 'edit'> = plan.sharedPermissions ? { ...plan.sharedPermissions } : {}

    for (const userId of userIds) {
      sharedPermissions[userId] = permissions
    }

    // Sync collaborators array with sharedPermissions
    // Note: We store minimal collaborator info (userId, role) for permission checks.
    // Full user details (name, email, avatar) are fetched on-demand from auth service.
    const existingCollaborators = plan.collaborators || []
    const collaborators = [...existingCollaborators]

    for (const userId of userIds) {
      const existingIndex = collaborators.findIndex(c => c.userId === userId)
      const role = permissions === 'edit' ? 'editor' : 'viewer'

      if (existingIndex >= 0) {
        // Update existing collaborator role
        collaborators[existingIndex] = { ...collaborators[existingIndex], role }
      } else {
        // Add new collaborator with minimal info
        collaborators.push({
          userId,
          role
        })
      }
    }

    const updated = await this.repository.updatePlan(planId, {
      sharedWith,
      sharedPermissions,
      collaborators
    })

    // Invalidate cache
    await this.invalidateCache(`${this.cachePrefix}${planId}`)
    await this.invalidateDashboardCache(plan.userId)
    logger.debug('Plan shared successfully', { planId, userIds })

    return updated
  }

  // Get CRDT sync state
  async getSyncState(planId: string): Promise<string> {
    // ✅ Return empty state if collaboration disabled
    if (process.env.ENABLE_COLLABORATION === 'false') {
      return ''
    }
    logger.debug('Fetching sync state', { planId })

    const state = await this.redis.get(`ydoc:${planId}`)

    if (!state) {
      // Initialize if not exists
      logger.debug('No sync state found, initializing CRDT', { planId })
      await this.initializeCRDT(planId)
      const newState = await this.redis.get(`ydoc:${planId}`)
      return newState ? Buffer.from(newState).toString('base64') : ''
    }

    return Buffer.from(state).toString('base64')
  }

  // Update CRDT sync state
  async updateSyncState(planId: string, stateBase64: string): Promise<void> {
    // ✅ Skip if collaboration disabled
    if (process.env.ENABLE_COLLABORATION === 'false') return
    logger.debug('Updating sync state', { planId, payloadSize: stateBase64.length })

    const stateBuffer = Buffer.from(stateBase64, 'base64')
    await this.redis.set(`ydoc:${planId}`, stateBuffer)
    logger.info('Sync state updated', { planId })
  }

  private async createDefaultPlan(userId: string): Promise<Plan> {
    logger.info('Creating default plan for user', { userId })
    const dto: CreatePlanDTO = {
      name: 'Mon voyage',
      description: 'Plan par défaut',
      isPublic: false,
      isDefault: true
    }

    const plan = await this.repository.createPlan(dto, userId)
    await this.repository.setDefaultPlan(userId, plan.id)
    await this.invalidateCache(
      `${this.cachePrefix}user:${userId}`,
      `${this.cachePrefix}${plan.id}`
    )
    await this.invalidateDashboardCache(userId)

    const reloaded = await this.repository.findPlanById(plan.id)
    logger.debug('Default plan created', { userId, planId: plan.id })
    return reloaded ?? plan
  }

  private async withCRDTDocument(planId: string, mutate: (doc: Y.Doc) => void): Promise<void> {
    if (process.env.ENABLE_COLLABORATION === 'false') {
      return
    }

    try {
      const key = `ydoc:${planId}`
      let stored = await this.redis.getBuffer(key)

      if (!stored) {
        await this.initializeCRDT(planId)
        stored = await this.redis.getBuffer(key)
      }

      const doc = new Y.Doc()

      if (stored) {
        Y.applyUpdate(doc, new Uint8Array(stored))
      } else {
        const planMap = doc.getMap('plan')
        planMap.set('id', planId)
        planMap.set('version', 0)
      }

      mutate(doc)

      const encoded = Y.encodeStateAsUpdate(doc)
      await this.redis.set(key, Buffer.from(encoded))
    } catch (error) {
      logger.error('Failed to persist CRDT document', { planId, error })
    }
  }

  private serializeStageForCRDT(stage: Stage): Record<string, unknown> {
    return JSON.parse(
      JSON.stringify(stage, (_key, value) => {
        if (value instanceof Date) {
          return value.toISOString()
        }
        return value
      })
    )
  }

  private bumpPlanVersion(doc: Y.Doc): void {
    const planMap = doc.getMap<any>('plan')
    const currentVersion = Number(planMap.get('version') ?? 0)
    planMap.set('version', currentVersion + 1)
    planMap.set('updatedAt', new Date().toISOString())
  }

  private async upsertStageInCRDT(planId: string, stage: Stage): Promise<void> {
    await this.withCRDTDocument(planId, doc => {
      const stagesMap = doc.getMap<any>('stages')
      stagesMap.set(stage.id, {
        ...this.serializeStageForCRDT(stage),
        _meta: {
          updatedAt: new Date().toISOString(),
          source: 'api'
        }
      })
      this.bumpPlanVersion(doc)
    })
  }

  private async removeStageFromCRDT(planId: string, stageId: string): Promise<void> {
    await this.withCRDTDocument(planId, doc => {
      const stagesMap = doc.getMap<any>('stages')
      if (stagesMap.has(stageId)) {
        stagesMap.delete(stageId)
        this.bumpPlanVersion(doc)
      }
    })
  }

  private async syncStagesWithCRDT(planId: string, stages: Stage[]): Promise<void> {
    await this.withCRDTDocument(planId, doc => {
      const stagesMap = doc.getMap<any>('stages')

      for (const key of Array.from(stagesMap.keys())) {
        stagesMap.delete(key)
      }

      for (const stage of stages) {
        stagesMap.set(stage.id, {
          ...this.serializeStageForCRDT(stage),
          _meta: {
            updatedAt: new Date().toISOString(),
            source: 'api'
          }
        })
      }

      this.bumpPlanVersion(doc)
    })
  }

  private safeParseDate(value?: string): Date | undefined {
    if (!value) return undefined
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? undefined : parsed
  }

  private describePrompt(payload: GeneratePlanDTO): string {
    const destinations = payload.destinations.join(' → ')
    return `${payload.origin} → ${destinations} · ${payload.days}j · ${payload.travelMode} · ${payload.pace}`
  }
}
